import React, { useState, useRef, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import {
  FiMenu,
  FiChevronLeft,
  FiChevronRight,
  FiSearch,
  FiBell,
  FiSun,
  FiMoon,
  FiArrowRightCircle,
  FiUser,
  FiSettings,
  FiLogOut,
} from 'react-icons/fi'
import { logoutTeacher } from '../store/slices/authSlice'
import { setTheme } from '../store/slices/themeSlice'
import './Header.css'

const themeOrder = ['samsung', 'sunrise', 'aurora', 'emerald', 'midnight']

const Header = ({ onToggleSidebar, onToggleCollapse, isSidebarCollapsed }) => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { user } = useSelector((state) => state.auth)
  const currentTheme = useSelector((state) => state.theme.current)

  const [showProfileMenu, setShowProfileMenu] = useState(false)
  const [showNotifications, setShowNotifications] = useState(false)
  const profileMenuRef = useRef(null)
  const notificationRef = useRef(null)

  const handleThemeStep = () => {
    const currentIndex = themeOrder.indexOf(currentTheme)
    const nextIndex = currentIndex === -1 ? 0 : (currentIndex + 1) % themeOrder.length
    dispatch(setTheme(themeOrder[nextIndex]))
  }

  const ThemeIcon = useMemo(() => (currentTheme === 'midnight' ? FiSun : FiMoon), [currentTheme])

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target)) {
        setShowProfileMenu(false)
      }
      if (notificationRef.current && !notificationRef.current.contains(event.target)) {
        setShowNotifications(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleLogout = () => {
    dispatch(logoutTeacher())
    navigate('/login')
  }

  return (
    <header className="app-header">
      <div className="app-header__left">
        <button type="button" className="icon-button only-mobile" onClick={onToggleSidebar} aria-label="Toggle navigation">
          <FiMenu size={20} />
        </button>
        <button type="button" className="icon-button only-desktop" onClick={onToggleCollapse} aria-label={isSidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}>
          {isSidebarCollapsed ? <FiChevronRight size={20} /> : <FiChevronLeft size={20} />}
        </button>
        <div className="header-breadcrumb">
          <span>Teacher Command Center</span>
          <h1>Daily Orchestrator</h1>
        </div>
      </div>

      <div className="app-header__right">
        <div className="search-field">
          <FiSearch size={18} />
          <input type="search" placeholder="Search timeline, people, lectures..." aria-label="Search" />
        </div>

        <button type="button" className="icon-button" onClick={handleThemeStep} aria-label="Cycle theme">
          <ThemeIcon size={18} />
          <span className="icon-button__label">{currentTheme}</span>
        </button>

        <div className="notification-panel" ref={notificationRef}>
          <button type="button" className="icon-button" onClick={() => setShowNotifications((prev) => !prev)} aria-label="Toggle notifications">
            <FiBell size={18} />
            <span className="pill">3</span>
          </button>
          {showNotifications && (
            <div className="flyout">
              <div className="flyout__header">
                <div>
                  <h4>Pulse Feed</h4>
                  <p>Live updates from your batches</p>
                </div>
                <button type="button" className="text-button">Mark as viewed</button>
              </div>
              <div className="flyout__list">
                <article className="flyout__item unread">
                  <h5>New student enrolled</h5>
                  <p>John Doe joined Web Development batch</p>
                  <span>2 minutes ago</span>
                </article>
                <article className="flyout__item">
                  <h5>Lecture reminder</h5>
                  <p>React Components deep dive starts in 30 minutes</p>
                  <span>28 minutes ago</span>
                </article>
                <article className="flyout__item">
                  <h5>Assignment insight</h5>
                  <p>5 students submitted JavaScript project</p>
                  <span>1 hour ago</span>
                </article>
              </div>
              <button type="button" className="flyout__footer">
                View full notification center <FiArrowRightCircle size={16} />
              </button>
            </div>
          )}
        </div>

        <div className="profile-panel" ref={profileMenuRef}>
          <button type="button" className="profile-chip" onClick={() => setShowProfileMenu((prev) => !prev)}>
            <div className="profile-chip__avatar">
              <span>{user?.firstName?.[0]}{user?.lastName?.[0]}</span>
            </div>
            <div className="profile-chip__meta">
              <strong>{user?.firstName} {user?.lastName}</strong>
              <span>{user?.role || 'Lead Instructor'}</span>
            </div>
          </button>
          {showProfileMenu && (
            <div className="profile-flyout">
              <header>
                <div className="profile-chip__avatar large">
                  <span>{user?.firstName?.[0]}{user?.lastName?.[0]}</span>
                </div>
                <div>
                  <h4>{user?.firstName} {user?.lastName}</h4>
                  <p>{user?.email}</p>
                  <span className="badge">{user?.role || 'Teacher'}</span>
                </div>
              </header>
              <nav>
                <button type="button" onClick={() => { navigate('/profile'); setShowProfileMenu(false) }}>
                  <FiUser size={18} />
                  My profile
                </button>
                <button type="button" onClick={() => { navigate('/settings'); setShowProfileMenu(false) }}>
                  <FiSettings size={18} />
                  Studio preferences
                </button>
              </nav>
              <footer>
                <button type="button" onClick={handleLogout}>
                  <FiLogOut size={18} />
                  Sign out
                </button>
              </footer>
            </div>
          )}
        </div>
      </div>
    </header>
  )
}

export default Header